import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, PackageCheck, ShoppingBag } from 'lucide-react';
import { trackOutboundLink } from '../analytics';

const shopifyUrl = 'https://oceanbeachbrands.myshopify.com';
const journeyAerialWebp = '/zeronimo-journey-night-aerial.webp';
const journeyAerialJpg = '/zeronimo-journey-night-aerial.jpg';

const ZeronimoJourney = () => {
  return (
    <section id="journey" className="relative scroll-mt-24 overflow-hidden border-t border-gray-900 bg-black px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,14,18,0.96),rgba(0,0,0,1))]" />

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.35fr] lg:items-end">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#8fa8b6]/25 bg-[#8fa8b6]/[0.06] px-4 py-2 text-sm font-semibold text-[#bfd0d9]"
            >
              <span className="h-2 w-2 rounded-full bg-[#9eb6c3]" />
              Zeronimo fulfillment update
            </motion.p>

            <h2 className="font-sans-luxury text-4xl font-black leading-none text-gray-100 sm:text-6xl">
              Landed in Connecticut
            </h2>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-gray-300 sm:text-xl">
              Zeronimo has landed in Connecticut, and customer orders are now shipping from the warehouse.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="border border-[#8fa8b6]/25 bg-[#8fa8b6]/[0.06] px-5 py-4">
              <p className="text-sm text-gray-500">Now</p>
              <p className="mt-1 text-lg font-semibold text-[#d2dde3]">Landed in Connecticut</p>
            </div>
            <div className="border border-white/10 bg-white/[0.03] px-5 py-4">
              <p className="text-sm text-gray-500">Orders</p>
              <p className="mt-1 text-lg font-semibold text-white">Now shipping</p>
            </div>
            <div className="border border-white/10 bg-white/[0.03] px-5 py-4">
              <p className="text-sm text-gray-500">Journey</p>
              <p className="mt-1 text-lg font-semibold text-white">NYC to Connecticut complete</p>
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr_0.5fr]">
          <div
            role="img"
            aria-label="Night aerial view of New York and Connecticut marking the completed Zeronimo journey"
            className="relative min-h-[360px] overflow-hidden border border-white/10 bg-black sm:aspect-[16/9]"
          >
            <picture>
              <source srcSet={journeyAerialWebp} type="image/webp" />
              <img
                src={journeyAerialJpg}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover opacity-60 brightness-75 contrast-125 saturate-[0.82]"
              />
            </picture>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_20%,rgba(0,0,0,0.45)_58%,rgba(0,0,0,0.95)_96%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.62),rgba(0,0,0,0.12)_44%,rgba(0,0,0,0.82))]" />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.68),rgba(0,0,0,0.08)_44%,rgba(0,0,0,0.62))]" />
            <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black to-transparent" />

            <div className="absolute left-[12%] top-[64%] sm:left-[14%] sm:top-[66%]">
              <p className="font-sans-luxury text-2xl font-semibold tracking-normal text-gray-300/85 sm:text-4xl">
                New York
              </p>
              <div className="mt-2 h-px w-20 bg-gray-300/35" />
            </div>
            <div className="absolute right-[10%] top-[20%] text-right sm:right-[12%] sm:top-[22%]">
              <p className="font-sans-luxury text-2xl font-semibold tracking-normal text-gray-200/90 sm:text-4xl">
                Connecticut
              </p>
              <div className="ml-auto mt-2 h-px w-24 bg-gray-200/35" />
            </div>
          </div>

          <aside className="flex flex-col justify-between border border-white/10 bg-[#070707] p-6 sm:p-7">
            <div>
              <div className="flex items-center gap-3 text-[#bfd0d9]">
                <PackageCheck size={20} />
                <p className="text-base font-semibold">Connecticut arrival confirmed</p>
              </div>
              <p className="mt-5 text-2xl font-semibold leading-tight text-white">
                Landed. Released. Now shipping.
              </p>
              <p className="mt-4 text-base leading-relaxed text-gray-400">
                After the completed New York City to Connecticut handoff, the wine is in fulfillment and customer orders are leaving the warehouse.
              </p>
            </div>

            <div className="mt-8">
              <a
                href={shopifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Shop Zeronimo — now shipping"
                onClick={() => trackOutboundLink(shopifyUrl, 'Shop Zeronimo Now Shipping', { section: 'zeronimo_shipping_journey' })}
                className="flex items-center justify-between gap-4 border border-[#8fa8b6]/40 bg-[#8fa8b6]/[0.08] px-5 py-5 text-left text-base font-semibold text-[#e2ebef] transition-colors duration-300 hover:border-[#bfd0d9]/70 hover:bg-[#8fa8b6]/[0.14] sm:text-lg"
              >
                <span className="flex items-center gap-4">
                  <ShoppingBag size={20} />
                  <span className="flex flex-col gap-1">
                    <span className="text-[0.72rem] font-black uppercase tracking-[0.2em] text-[#9eb6c3] sm:text-xs">Now Shipping</span>
                    <span className="font-sans-luxury text-xl font-semibold leading-none tracking-normal text-white sm:text-2xl">Shop Zeronimo</span>
                  </span>
                </span>
                <ExternalLink size={18} />
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default ZeronimoJourney;
